import { businessShape, openSpotsMessage, type MyBusiness, type OwnerOpenSpots } from "@teamna/shared";
import { track } from "@teamna/web-client/analytics/web.ts";
import { useOpenSpots } from "@teamna/web-client/hooks/use-families.ts";
import { formatTime, formatDayShort } from "@teamna/web-client/lib/format.ts";
import { CopyMessageButton } from "../../components/index.ts";

/**
 * Open spots to fill (Studio Home H2): where clients book for themselves, one card on Home counts
 * the free places of the next 48 hours and gives one button: copy a WhatsApp message with each
 * time and its Book link (`openSpotsMessage`). The owner pastes it in the studio's group. The card
 * is not there when no place is open, and a family business never has it.
 */
export function OpenSpotsCard({ business }: { business: MyBusiness }) {
  const self = businessShape(business).bookingFor === "self";
  const query = useOpenSpots(business.businessId, self);
  if (!self || !query.data || query.data.sessions.length === 0) return null;
  return <OpenSpotsView spots={query.data} businessName={business.name} timezone={business.timezone} />;
}

/** The card itself, from plain props, so Storybook draws every state. */
export function OpenSpotsView({
  spots,
  businessName,
  timezone,
  initialCopied = false,
}: {
  spots: OwnerOpenSpots;
  businessName: string;
  timezone: string;
  /** Storybook: show the copied state. */
  initialCopied?: boolean;
}) {
  const sessionsWord = spots.sessions.length === 1 ? "session" : "sessions";
  return (
    <section aria-labelledby="open-spots-title" className="rounded-card border border-line bg-paper px-5 py-4 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 id="open-spots-title" className="text-[19px]">
            {spots.spots} open {spots.spots === 1 ? "place" : "places"} in the next {spots.windowHours} hours
          </h2>
          <p className="mt-0.5 text-[13.5px] text-ink-2">
            In {spots.sessions.length} {sessionsWord}. Copy one message with each time and its Book link, and send it on WhatsApp.
          </p>
        </div>
        <CopyMessageButton
          text={openSpotsMessage({ businessName, timezone, sessions: spots.sessions })}
          label="Copy WhatsApp message"
          initialCopied={initialCopied}
          onCopied={() => track("open_spots_shared", { spots_count: spots.spots, sessions_count: spots.sessions.length, channel: "copy", from: "home" })}
        />
      </div>
      <ul className="mt-3 flex flex-col divide-y divide-line border-t border-line">
        {spots.sessions.map((session) => (
          <li key={session.id} className="flex items-center gap-3 py-2 text-[14px]">
            <span aria-hidden="true" className="size-2.5 shrink-0 rounded-full" style={{ background: session.groupColor ?? "var(--color-tile-booked)" }} />
            <span className="w-28 shrink-0 tabular-nums text-ink-2">
              {formatDayShort(session.startsAt, timezone)} {formatTime(session.startsAt, timezone)}
            </span>
            <span className="min-w-0 flex-1 truncate font-semibold text-ink" dir="auto">
              {session.groupName}
            </span>
            <span className="shrink-0 font-semibold text-tile-booked-ink">
              {session.open} {session.open === 1 ? "place" : "places"}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
